import { createSelector } from "@reduxjs/toolkit";
import { startOfDay, isBefore, isSameDay, format } from "date-fns";
import { RootState } from "../store";
import { Prompt } from "../../types/prompts";
import { sortedPromptsByWeeklyScores } from "../utils/sortedPrompts";
import { scoreWeeklyFromDailyPromptSeries } from "../scores/utils/scoreWeeklyFromDailyPromptSeries";
import { parseLocalDateString } from "../utils/date/calendarDate";

export const selectLoading = (state: RootState) => state.prompts.loading;
export const selectPromptsLoadError = (state: RootState) => state.prompts.loadError;
export const selectPromptsState = (state: RootState) => state.prompts.items;
export const selectActivePromptsState = (state: RootState) => state.prompts.items?.filter(prompt => prompt.is_active) || null;
export const selectInactivePromptsState = (state: RootState) => state.prompts.items?.filter(prompt => !prompt.is_active && !prompt.deleted_at) || null;

export const selectActivePromptsCount = createSelector(
  selectPromptsState,
  (prompts) => prompts?.filter((prompt: Prompt) => prompt.is_active).length,
);

export const selectTotalPromptsCount = createSelector(
  selectPromptsState,
  (prompts) => prompts?.filter((prompt: Prompt) => !prompt.deleted_at).length || 0,
);

export const selectActiveByDatePromptsState = (date: string | undefined) =>
  createSelector(
    (state: RootState) => state.prompts.items,
    (prompts) => {
      if (!prompts || !date) return null;

      const selectedDate = parseLocalDateString(date);
      if (Number.isNaN(selectedDate.getTime())) return null;

      return prompts.filter((prompt: Prompt) => {
        if (!prompt) return false;

        const promptCreatedDate = startOfDay(new Date(prompt.created_at));
        const promptDeletedDate = prompt.deleted_at ? startOfDay(new Date(prompt.deleted_at)) : null;
        const afterCreated = isSameDay(selectedDate, promptCreatedDate) || !isBefore(selectedDate, promptCreatedDate);
        const beforeDeleted = !promptDeletedDate || isSameDay(selectedDate, promptDeletedDate) || isBefore(selectedDate, promptDeletedDate);

        return afterCreated && beforeDeleted;
      });
    },
  );



  export const selectByDatePromptsSortedByDailyAverageScoresState = (brandId: number, date: string | undefined) =>
    createSelector(
      (state: RootState) => state.prompts.items,
      (state: RootState) => state.scores.dailyAveragePrompt.data,
      (prompts, dailyScoresData) => {
        if (!prompts || !date || !dailyScoresData) return null;

        const promptsByDate = prompts.filter((prompt) => prompt.is_active && !prompt.deleted_at);
        const promptsWithScores = promptsByDate.map((prompt) => {
          const promptScores = dailyScoresData[String(prompt.id)];
          const brandScores = promptScores?.[String(brandId)];
          const scoreForDate = brandScores?.scores?.find((score) => format(score.created_at, "yyyy-MM-dd") === date);

          return {
            ...prompt,
            dailyScore: scoreForDate?.visibility || 0,
          };
        });

        return promptsWithScores.sort((a, b) => b.dailyScore - a.dailyScore);
      },
    );




export const selectActivePromptsByBrandState = (id: string | undefined) =>
  createSelector(
    (state: RootState) => state.prompts.items,
    (state: RootState) => state.scores.dailyAveragePrompt.data,
    (prompts, dailyByPrompt) => {
      if (!prompts || !id || !dailyByPrompt) return null;

      const brandId = Number(id);

      const activePrompts = prompts
        .filter((prompt: Prompt) => prompt && prompt.is_active)
        .map((prompt: Prompt) => {
          const item = dailyByPrompt[String(prompt.id)]?.[String(brandId)];
          const weeklyScore = scoreWeeklyFromDailyPromptSeries(
            brandId,
            Number(prompt.id),
            item,
          );

          return {
            ...prompt,
            title: prompt.content,
            weeklyScores: weeklyScore,
          };
        });

      return sortedPromptsByWeeklyScores(activePrompts);
    },
  );

export const selectActivePromptsWithScoresState = createSelector(
  (state: RootState) => state.prompts.items,
  (state: RootState) => state.scores.dailyAveragePrompt.data,
  (prompts, scores) => {
    if (!prompts || !scores) return null;

    const activePrompts = prompts.filter((prompt: Prompt) =>
      prompt && prompt.is_active
    );

    return activePrompts.map((prompt: Prompt) => {
      const currentScore = scores[prompt.id];

      return {
        ...prompt,
        title: prompt.content,
        scores: currentScore,
      };
    });
  },
);
